// TODO
// - Separate and refactor Selectors once settled

import { cloneDeep, get } from "lodash";
import { createSelector } from "reselect";

import { AT_TYPES, AT_TYPES_TO_KEYS } from "../../../constants/entity.js";
import { MARKING_STATES } from "../../../utils/marking.js";
import { ICON_MAP, ICON_IMAGE_MAP } from "../../../constants/iconMap.js";
import { IMAGE_VENDORS } from "../../../constants/images.js";
import { OPERATIONS } from "../../../constants/operations.js";

import operationSelector from "../operations/operationSelector.js";
import {
  addResourceOperationsAvailable,
  operationAvailability,
  operationAvailable
} from "../../../utils/operations.js";
import endpointSelector, {
  endpointsQuery,
  permissionsQuery
} from "../endpoints/endpointSelector.js";

// -- HELPERS -- //

function generateSimpleResourceMap(queryResult, useImage = false) {
  return queryResult.map(item => {
    const resourceItem = {
      key: item.id,
      value: item["@id"],
      service: item.services,
      text: item.name,
      readOnly: item.readonly
    };

    if (useImage) {
      const imageKey = `${item.operatingSystem}${
        item.vendor && item.vendor === IMAGE_VENDORS.GOLDEN ? "Golden" : ""
      }`;

      resourceItem.image = {
        avatar: true,
        src: ICON_IMAGE_MAP[imageKey],
        size: "mini"
      };
    } else {
      resourceItem.icon = ICON_MAP[item.operatingSystem];
    }

    return resourceItem;
  }, []);
}

function generateSimpleValueOptionObject(value) {
  return {
    key: value,
    value,
    text: value
  };
}

// -- STATE QUERIES -- //

const projectsDataMapQuery = state => state.projects.dataMap;
const providersDataMapQuery = state => state.providers.dataMap;
const regionsDataMapQuery = state => state.regions.dataMap;

const servicesQuery = state => state.services.data;
const regionsQuery = state => state.regions.data;
const zonesQuery = state => state.zones.data;
const imagesQuery = state => get(state, "images.data", undefined);
const sizesQuery = state => state.sizes.data;

const projectCreateOperationQuery = state =>
  endpointSelector.selectOperationByTypeAndName(state)(
    AT_TYPES.PROJECT,
    OPERATIONS.CREATE
  );

const projectCreateFieldsQuery = state => {
  const createOperation = projectCreateOperationQuery(state);
  return createOperation?.fields || [];
};

const cidrFieldsFromProjectCreateEndpoint = (state, props) => {
  const field = projectCreateFieldsQuery(state).find(
    element => element.name === "cidrClassPrefix"
  );

  return field?.options || [];
};

// -- PROPS QUERIES -- //

const projectIdFromPropsQuery = (state, props) => {
  let id = get(props, "projectId", null);
  if (!id) {
    const matchId = get(props, "match.params.id", null);
    id = matchId ? `/projects/${matchId}` : null;
  }
  return id;
};

const defaulRequiresUserVerificationFromProjectCreateEndpoint = state => {
  const field = projectCreateFieldsQuery(state).find(
    element => element.name === "requiresUserVerification"
  );

  if (!field) {
    return undefined;
  }

  return field.value;
};

// -- FORMIK PROPS QUERIES -- //

const regionIdFromFormikPropsQuery = (state, props) => {
  if (!get(props, "formikProps", null)) {
    // console.warn(
    //   "projectWizardSelectors: regionIdFromFormikPropsQuery will always return null if formikProps is not set"
    // );
  }
  return get(props, "formikProps.values.region", null);
};

const providerIdFromFormikPropsQuery = (state, props) => {
  if (!get(props, "formikProps", null)) {
    // console.warn(
    //   "projectWizardSelectors: providerIdFromFormikPropsQuery will always return null if formikProps is not set"
    // );
  }
  return get(props, "formikProps.values.provider", null);
};

const imageIdFromFormikPropsQuery = (state, props) => {
  if (!get(props, "formikProps", null) || !get(props, "id", null)) {
    // console.warn(
    //   "projectWizardSelectors: imageIdFromFormikPropsQuery will always return null if props.formikProps or props.id is not set"
    // );
  }
  return get(props, `formikProps.values.${props.id}.image`, null);
};

const sizeIdFromFormikPropsQuery = (state, props) => {
  if (!get(props, "formikProps", null) || !get(props, "id", null)) {
    // console.warn(
    //   "projectWizardSelectors: sizeIdFromFormikPropsQuery will always return null if props.formikProps or props.id is not set"
    // );
  }
  return get(props, `formikProps.values.${props.id}.size`, null);
};

const firstOctetFromFormikPropsQuery = (state, props) => {
  if (!get(props, "formikProps", null) || !get(props, "id", null)) {
    // console.warn(
    //   "projectWizardSelectors: firstOctetFromFormikPropsQuery will always return null if props.formikProps or props.id is not set"
    // );
  }
  return get(props, `formikProps.values.${props.id}Prime`, null);
};

// -- PROPS QUERIES -- //

const typeKeyFromResourceTypePropQuery = (state, props) => {
  if (props.resourceType) return AT_TYPES_TO_KEYS[props.resourceType];
  // console.warn(
  //   "projectWizardSelectors: typeKeyFromResourceTypePropQuery will always return null if props.resourceType is not set"
  // );
  return null;
};

const providerIdFromProviderPropsQuery = (state, props) => {
  const provider = get(props, "provider", null);
  if (!provider) {
    // console.warn(
    //   "projectWizardSelectors: typeKeyFromResourceTypePropQuery will always return null if props.provider is not set"
    // );
  }
  return provider;
};

// -- SELECTORS -- //

const providerIdSelector = createSelector(
  [
    projectIdFromPropsQuery,
    projectsDataMapQuery,
    providerIdFromFormikPropsQuery
  ],
  (projectId, projects, providerId) => {
    if (providerId) {
      return providerId;
    } else if (projectId) {
      // currently there will only be one provider available
      // temporary while the main system can't use multiple providers
      if (projects) {
        return get(projects, `[${projectId}].providers[0]`, null);
      }
      // console.warn("WARN: No project data found for ", projectId);
      return null;
    }
    // console.warn("WARN: No valid provider found");
    return null;
  }
);

export const regionIdSelector = createSelector(
  [projectIdFromPropsQuery, projectsDataMapQuery, regionIdFromFormikPropsQuery],
  (projectId, projects, regionId) => {
    if (regionId) {
      return regionId;
    } else if (projectId) {
      // currently there will only be one region available
      // temporary while the main system can't use multiple regions
      if (projects) {
        return get(projects, `[${projectId}].regions[0]`, null);
      }
      // console.warn("WARN: No project data found for ", projectId);
      return null;
    }
    // console.warn("WARN: No valid region found");
    return null;
  }
);

export const selectProvidersList = state => {
  // remove readonly providers
  const filteredProviders = state.providers.data.filter(provider => {
    return !provider.readonly;
  });

  return generateSimpleResourceMap(filteredProviders);
};

export const selectRegionsList = createSelector(
  [providersDataMapQuery, servicesQuery, regionsQuery, providerIdSelector],
  (providers, services, regions, providerId) => {
    if (!providerId) return [];

    const providerServices = providers[providerId].services;
    // hard-code "NodeService" as key for now
    const service = services.filter(item => {
      return (
        item["@type"] === AT_TYPES.SERVICES.NODE &&
        providerServices.indexOf(item["@id"]) > -1
      );
    });
    // There should only be 1 service returned
    let filteredRegions = regions.filter(item =>
      item.services.some(element => element === service[0]["@id"])
    );

    // keeping separate as above service filter should be removed after future api updates
    // remove readonly regions
    filteredRegions = filteredRegions.filter(region => {
      return !region.readonly;
    });

    return generateSimpleResourceMap(filteredRegions);
  }
);

export const selectZonesList = createSelector(
  [zonesQuery, regionIdSelector],
  (zones, regionId) => {
    if (!zones || !regionId) return [];

    let filteredZones = zones.filter(item => {
      return item.region === regionId;
    });

    const test = generateSimpleResourceMap(filteredZones);

    return test;
  }
);

// // currently there will only be one region available
// // temporary while the main system can't use multiple regions
export const selectRegion = createSelector(
  [regionIdSelector, regionsDataMapQuery],
  (regionId, regions) => {
    if (regionId) {
      return regions[regionId];
    }
    return null;
  }
);

// // currently there will only be one provider available
// // temporary while the main system can't use multiple providers
export const selectProvider = createSelector(
  [providerIdSelector, providersDataMapQuery],
  (providerId, providers) => {
    if (providerId) {
      return providers[providerId];
    }
    return null;
  }
);

export const selectServiceByProviderAndType = createSelector(
  [
    servicesQuery,
    providerIdFromProviderPropsQuery,
    typeKeyFromResourceTypePropQuery
  ],
  (services, providerId, typeKey) => {
    const atType = AT_TYPES.SERVICES[typeKey];

    let filteredServices = services.filter(item => item["@type"] === atType);

    if (providerId) {
      return filteredServices.filter(item => item.provider === providerId)[0];
    }

    console.warn("WARN: No valid service found");
    return null;
  }
);

// problem here - how to deal with no resourceTypeByPropsId
export const selectImagesList = createSelector(
  [imagesQuery, regionIdSelector, typeKeyFromResourceTypePropQuery],
  (images, regionId, typeKey) => {
    const atTypes = AT_TYPES.IMAGES[typeKey];
    let filteredImages = images.filter(item => {
      if (atTypes.includes(item["@type"])) {
        // if marking is available it is an image rather than chef so must be converged before it is available for use
        // should probably be shifted to the API so it is never passed in the first place?
        const isAnAvailableMarking = item.marking
          ? item.marking === MARKING_STATES.CONVERGED
          : true;

        // remove readonly images
        const isReadable = !item.readonly;

        return isAnAvailableMarking && isReadable;
      }
    });

    if (regionId) {
      filteredImages = filteredImages.filter(item =>
        item.regions.some(element => element === regionId)
      );
    }

    if (filteredImages) {
      return generateSimpleResourceMap(filteredImages, true);
    }
    return [];
  }
);

export const selectSizesList = createSelector(
  [sizesQuery, regionIdSelector, typeKeyFromResourceTypePropQuery],
  (sizes, regionId, typeKey) => {
    const atType = AT_TYPES.SIZES[typeKey];
    // remove readonly sizes
    let filteredSizes = sizes.filter(item => {
      if (item["@type"] === atType) {
        // initially present to implement sunset functionality
        return !item.readonly;
      }
    });

    if (regionId) {
      filteredSizes = filteredSizes.filter(item =>
        item.regions.some(element => element === regionId)
      );
    }

    if (filteredSizes) {
      return generateSimpleResourceMap(filteredSizes);
    }
    return [];
  }
);

export const selectImageId = createSelector(
  [imageIdFromFormikPropsQuery],
  imageId => {
    return imageId;
  }
);

export const selectImage = createSelector(
  [imagesQuery, imageIdFromFormikPropsQuery],
  (images, imageId) => {
    if (!images || !imageId) return {};

    const image = images.find(item => item["@id"] === imageId);

    return image || {};
  }
);

export const selectSize = createSelector(
  [sizesQuery, sizeIdFromFormikPropsQuery],
  (sizes, sizeId) => {
    if (!sizes || !sizeId) return {};

    const size = sizes.find(item => item["@id"] === sizeId);

    return size || {};
  }
);

export const selectVolumes = createSelector(
  [imagesQuery, imageIdFromFormikPropsQuery],
  (images, imageId) => {
    if (!images || !imageId) return [];

    const image = images.find(item => item["@id"] === imageId);

    if (!image) return [];

    const imageVolume = get(image, "volume", undefined);

    return Array.isArray(imageVolume) ? imageVolume : [imageVolume];
  }
);

// -- CIDR SELECTORS -- //

export const selectSimpleCIDROptions = createSelector(
  [cidrFieldsFromProjectCreateEndpoint],
  cidrFields => {
    if (!cidrFields) return [];

    const options = cidrFields.reduce((acc, item) => {
      const first = item.value.split(".")[0];

      if (!acc.find(el => el.key === first)) {
        acc.push({
          key: first,
          value: item.value,
          text: `${item.value}/16 prefix (${item.value}.0.0 - ${item.value}.255.255)`
        });
      }

      return acc;
    }, []);

    return options;
  }
);

export const selectFirstOctetOptions = createSelector(
  [cidrFieldsFromProjectCreateEndpoint],
  cidrFields => {
    if (!cidrFields) return [];

    const options = cidrFields.reduce((acc, item) => {
      const octets = item.value.split(".");
      const first = octets[0];

      if (!acc.find(el => el.value === first)) {
        acc.push(generateSimpleValueOptionObject(first));
      }

      return acc;
    }, []);

    return options;
  }
);

export const selectSecondOctetOptions = createSelector(
  [cidrFieldsFromProjectCreateEndpoint, firstOctetFromFormikPropsQuery],
  (cidrFields, firstOctet) => {
    if (!cidrFields || !firstOctet) return [];

    const options = cidrFields.reduce((acc, item) => {
      const octets = item.value.split(".");
      const first = octets[0];
      const second = octets[1];

      if (
        first === firstOctet &&
        !acc.find(option => option.value === second)
      ) {
        acc.push(generateSimpleValueOptionObject(second));
      }

      return acc;
    }, []);

    return options;
  }
);

export const selectDefaultRequiresUserVerification = createSelector(
  [defaulRequiresUserVerificationFromProjectCreateEndpoint],
  defaultRequiresUserVerification => defaultRequiresUserVerification
);

// -- PERMISSIONS & DISPLAY LOGIC -- //

const stepPermissionsFromPropsQuery = (state, props) => {
  // const stepGroup = props.steps[props.stepGroupId];
  const {
    config: { steps },
    stepNumber
  } = props;

  const panels = get(steps, `[${stepNumber}].panels`, null);

  if (!panels) return {};

  return panels.reduce((acc, panel) => {
    acc[panel.id] = {
      type: panel.resourceType,
      permission: panel.permissionType
    };
    return acc;
  }, {});
};

export const selectProjectOperations = createSelector(
  [projectsDataMapQuery, projectIdFromPropsQuery, permissionsQuery],
  (projects, projectId, permissions) => {
    if (!projects || !projectId) return undefined;

    const project = operationSelector.stampOperationPermissions(
      cloneDeep(projects[projectId]),
      permissions
    );

    return project.operations;
  }
);

export const selectStepAvailability = createSelector(
  [
    stepPermissionsFromPropsQuery,
    endpointsQuery,
    permissionsQuery,
    selectProjectOperations
  ],
  (stepPermissions, endpoints, permissions, operations) => {
    return Object.keys(stepPermissions).reduce((acc, key) => {
      const step = stepPermissions[key];

      // require a permissionType be set in the config
      if (!step.permission) {
        acc[key] = { permitted: false, enabled: false, available: false };
        return acc;
      }

      // prefer project level permissions
      const projectLevelAvailability = operationAvailability(
        step.permission,
        operations,
        false
      );

      if (projectLevelAvailability.available !== undefined) {
        acc[key] = projectLevelAvailability;
        return acc;
      }

      // check account level permissions if not
      const endpoint = endpoints.find(
        item => get(item, "manages.@type", null) === step.type
      );

      if (endpoint) {
        acc[key] = operationAvailable(
          step.permission,
          endpoint.operations,
          endpoint.enabledOperations,
          permissions
        );
        return acc;
      }

      // fall through to true if no permissions are available at any level
      acc[key] = { permitted: true, enabled: true, available: true };
      return acc;
    }, {});
  }
);

export const selectCreateProjectAvailability = state => {
  return endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.PROJECT,
    OPERATIONS.CREATE
  );
};

export const selectSubmitAvailability = createSelector(
  [selectCreateProjectAvailability, selectProjectOperations],
  (createProjectAvailability, operations) => {
    // prefer account level permissions
    if (createProjectAvailability && createProjectAvailability.available) {
      return true;
    }

    // check project level permissions if not
    return addResourceOperationsAvailable(operations);
  }
);

export const selectEstimateEndpointAvailability = state => {
  return endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.ESTIMATE,
    OPERATIONS.ESTIMATE
  );
};

export const selectEstimateAvailability = createSelector(
  [selectEstimateEndpointAvailability, selectProjectOperations],
  (estimateEndpointAvailabilty, projectOperations) => {
    // // prefer account level permissions
    if (estimateEndpointAvailabilty && estimateEndpointAvailabilty.available) {
      return true;
    }

    console.log("ProjectWizardSelectors.projectOperations:", projectOperations);

    // check project level permissions if not
    if (projectOperations) {
      const availability = operationAvailability(
        OPERATIONS.ESTIMATE,
        projectOperations,
        false
      );

      return availability.available;
    }

    return false;
  }
);

export default {
  selectVolumes,
  stepPermissionsFromPropsQuery,
  selectProjectOperations,
  selectStepAvailability,
  selectCreateProjectAvailability,
  selectSubmitAvailability,
  selectEstimateAvailability
};
